/**
 * What a search looks at on a row, decided once for every screen that has a
 * search box.
 *
 * No imports, so `today.ts` and `browse.ts` can both import it (with its
 * `.ts` extension, the way `today.ts` imports `places.ts`) and the root
 * suite can load it directly.
 *
 * This used to be two copies of one haystack — `today.ts`'s and `browse.ts`'s
 * own `restated` one — and both missed the people a row actually prints:
 * "Bresson" found nothing while four film rows on screen said "Robert
 * Bresson", because the director lives in `film.directors` and the haystack
 * read only `performers`. One function now, so the two search boxes cannot
 * disagree about what a row says.
 *
 * Every JSON column here is younger than some row in a warm IndexedDB cache
 * (CLAUDE.md's stale-cache trap), so every read is `?? []` / `?.` — a row
 * cached before a column existed simply contributes nothing for it.
 */

/** The row fields a search reads. `FeedRow` and `today.ts`'s `RowEntry` both
 *  satisfy it structurally. */
export interface Searchable {
  title: string
  venue?: string | null
  building?: string | null
  organisation?: string | null
  district?: string | null
  city?: string | null
  performers?: Record<string, string> | null
  film?: { directors?: string[] | null } | null
  classical?: {
    programme?: { composer?: string | null; work?: string | null }[] | null
    conductor?: string | null
    soloists?: { name: string }[] | null
    ensemble?: { name: string } | null
  } | null
  stage?: {
    author?: string | null
    composer?: string | null
    company?: string | null
    director?: string | null
    conductor?: string | null
    cast?: { name: string }[] | null
  } | null
  exhibition?: { artists?: string[] | null; curators?: string[] | null } | null
  night?: { lineup?: { name: string }[] | null } | null
  /** What a newsletter said about the event. Only `place_src` is read: the
   *  place as the mail named it ("Trabrennbahn Karlshorst"), which is what a
   *  card with no venue row prints on its where-line (`rowMeta.ts`'s
   *  `placeName`) — a search that cannot find what the card says is the
   *  "Bresson" bug again. Absent on a row cached before the column existed. */
  newsletter?: { place_src?: string | null } | null
}

/** Lowercased and stripped of diacritics, so "fuhrung" finds "Führung" and
 *  "dvorak" finds "Dvořák". A search that is exact about accents is a search
 *  nobody can type on a phone. */
export const fold = (s: string) =>
  s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

/** Every string on a row a search should look at: what it is, who is in it,
 *  and where. Not the blurb — the feed row does not carry one since WP 2.4,
 *  and a search that matched generated prose would be matching the one thing
 *  on screen that is not a fact. The people are every name the row's facts
 *  columns carry: a film's directors, a concert's conductor, soloists and
 *  ensemble, a production's director, author and cast, an exhibition's
 *  artists and curators, a club night's lineup — and the adapters' own
 *  `performers` reading, which is all an older row has. */
export function searchHaystack(e: Searchable): string {
  const parts: (string | null | undefined)[] = [
    e.title, e.venue, e.building, e.organisation, e.district, e.city,
  ]
  for (const v of Object.values(e.performers ?? {})) parts.push(v)
  for (const d of e.film?.directors ?? []) parts.push(d)
  const c = e.classical
  for (const item of c?.programme ?? []) { parts.push(item?.composer); parts.push(item?.work) }
  parts.push(c?.conductor, c?.ensemble?.name)
  for (const s of c?.soloists ?? []) parts.push(s?.name)
  const s = e.stage
  parts.push(s?.author, s?.composer, s?.company, s?.director, s?.conductor)
  for (const m of s?.cast ?? []) parts.push(m?.name)
  for (const a of e.exhibition?.artists ?? []) parts.push(a)
  for (const a of e.exhibition?.curators ?? []) parts.push(a)
  for (const l of e.night?.lineup ?? []) parts.push(l?.name)
  parts.push(e.newsletter?.place_src)
  return fold(parts.filter(p => typeof p === 'string' && p).join(' '))
}

/**
 * Does this row answer the query?
 *
 * Every whitespace-separated word has to appear somewhere on the row, which is
 * what makes "petrenko philharmonie" narrower than either half rather than
 * wider than both. A blank query matches everything.
 */
export function matchesSearch(e: Searchable, query: string): boolean {
  const q = fold(query).trim()
  if (!q) return true
  const hay = searchHaystack(e)
  return q.split(/\s+/).every(word => hay.includes(word))
}
